import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import { RootState } from "../../../../../../app/rootReducer";
import { useNotification } from "../../../../../../context/NotificationContext";
import { editUserAction } from "../../slices/usersSlice";
import {
  editUserSchema,
  type EditUserFormData,
} from "../../schemas/userSchemas";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getUserFormDetails } from "../../api/getUserFormDetailsApi";
import { UserFormDetails } from "../../api/types";

const EditUser: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const { loading: saveLoading } = useSelector(
    (state: RootState) => state.users,
  );
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );

  const [formDetails, setFormDetails] = useState<UserFormDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is editing their own account
  const isEditingSelf = userAppSettings?.employeeUuid === uuid;

  // Check if user being edited has a role that requires CertainPath assigner (e.g., Coach)
  const userCurrentRole = formDetails?.availableRoles.find(
    (r) => r.id === formDetails?.employeeBusinessRoleId,
  );
  const isRestrictedRole =
    userCurrentRole?.requiresCertainPathAssigner === true;
  const isFormDisabled = isRestrictedRole;

  const form = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      businessRoleId: undefined,
    },
    mode: "onChange",
  });

  const { control, setValue, handleSubmit, formState, watch } = form;
  const { isSubmitting, isValid } = formState;
  const selectedRoleId = watch("businessRoleId");

  useEffect(() => {
    const fetchFormDetails = async () => {
      if (!uuid) return;
      try {
        setLoading(true);
        const response = await getUserFormDetails(uuid);
        setFormDetails(response.data);

        // Populate form with existing values
        if (response.data.firstName) {
          setValue("firstName", response.data.firstName);
        }
        if (response.data.lastName) {
          setValue("lastName", response.data.lastName);
        }
        if (response.data.email) {
          setValue("email", response.data.email);
        }
        if (response.data.employeeBusinessRoleId) {
          setValue("businessRoleId", response.data.employeeBusinessRoleId);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load user details",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchFormDetails();
  }, [uuid, setValue]);

  const onSubmit = async (values: EditUserFormData) => {
    if (uuid) {
      try {
        await dispatch(
          editUserAction(uuid, {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            businessRoleId: values.businessRoleId,
          }),
        );
        showNotification(
          "Successfully updated user!",
          "The user information has been updated.",
          "success",
        );
      } catch {
        // Error is handled by axios interceptor
      }
    }
  };

  const availableRoles = formDetails?.availableRoles || [];
  // Filter out roles that require CertainPath assigner (e.g., Coach) from selectable options
  // But if form is disabled (viewing a Coach), show all roles so their current role displays
  const selectableRoles = isFormDisabled
    ? availableRoles
    : availableRoles.filter((role) => !role.requiresCertainPathAssigner);
  const selectedRole =
    availableRoles.find((r) => r.id === selectedRoleId) || null;

  if (!uuid) {
    return null;
  }

  return (
    <MainPageWrapper
      error={error}
      loading={loading || saveLoading}
      title="Edit User"
    >
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {isRestrictedRole && (
            <div className="flex items-start gap-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800 mb-4">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  This user is a CertainPath Coach
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Coach accounts are managed by CertainPath. Please contact
                  CertainPath to make changes to this user.
                </p>
              </div>
            </div>
          )}
          <div className="text-sm text-muted-foreground mb-2">
            Fields marked with an asterisk (*) are required.
          </div>
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input
                    disabled={isFormDisabled}
                    placeholder="Enter first name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input
                    disabled={isFormDisabled}
                    placeholder="Enter last name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input
                    disabled={isFormDisabled}
                    placeholder="Enter email address"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="businessRoleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business Role *</FormLabel>
                <Select
                  disabled={isEditingSelf || isFormDisabled}
                  onValueChange={(value) => {
                    const parsed = parseInt(value, 10);
                    if (!isNaN(parsed)) {
                      field.onChange(parsed);
                    }
                  }}
                  value={field.value ? field.value.toString() : undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {selectableRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id.toString()}>
                        {role.label}
                        {role.isCertainPathOnly && (
                          <span className="ml-2 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-2 py-0.5 rounded-full">
                            CP
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isEditingSelf && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800">
                    <Info className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      You cannot change your own role. To prevent locking
                      yourself out, ask another administrator to update your
                      role.
                    </p>
                  </div>
                )}
                {!isEditingSelf && selectedRole?.description && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">
                    <Info className="h-4 w-4 text-slate-500 dark:text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {selectedRole.description}
                    </p>
                  </div>
                )}
                {selectedRole?.permissions &&
                  selectedRole.permissions.length > 0 && (
                    <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                        This role includes:
                      </p>
                      <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                        {selectedRole.permissions.map((permission) => (
                          <li
                            className="flex items-center gap-2"
                            key={permission.id}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            {permission.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-4 pt-4">
            <Button
              onClick={() => navigate("/hub/users")}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={
                !isValid || isSubmitting || saveLoading || isFormDisabled
              }
              type="submit"
            >
              {isSubmitting || saveLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>
    </MainPageWrapper>
  );
};

export default EditUser;
